import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import DictationInput from '../../components/DictationInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  Building, Plus, Users, Phone, Mail, MapPin, 
  ChevronRight, TrendingUp, CheckCircle, Clock, ArrowLeft,
  BarChart3, UserPlus
} from 'lucide-react';

const REFERRAL_TYPE_LABELS = {
  probation: 'Probation',
  parole: 'Parole',
  treatment_center: 'Treatment Center',
  hospital: 'Hospital',
  therapist: 'Therapist',
  self_referral: 'Self-Referral',
  family: 'Family',
  other_agency: 'Other Agency',
  court: 'Court',
  social_services: 'Social Services',
};

const TYPE_COLORS = {
  probation: 'bg-blue-100 text-blue-700',
  parole: 'bg-purple-100 text-purple-700',
  treatment_center: 'bg-emerald-100 text-emerald-700',
  hospital: 'bg-red-100 text-red-700',
  therapist: 'bg-pink-100 text-pink-700',
  self_referral: 'bg-amber-100 text-amber-700',
  family: 'bg-cyan-100 text-cyan-700',
  other_agency: 'bg-slate-100 text-slate-700',
  court: 'bg-indigo-100 text-indigo-700',
  social_services: 'bg-teal-100 text-teal-700',
};

export default function ReferralSourcesPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [sources, setSources] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedSource, setSelectedSource] = useState(null);
  const [newSourceOpen, setNewSourceOpen] = useState(false);
  const [newReferralOpen, setNewReferralOpen] = useState(false);
  const [sourceForm, setSourceForm] = useState({ organization_name: '', referral_type: '', contact_name: '', phone: '', email: '', address: '', notes: '' });
  const [referralForm, setReferralForm] = useState({ referral_source_id: '', resident_name: '', resident_email: '', resident_phone: '', notes: '', priority: 'medium' });
  const [activeTab, setActiveTab] = useState('sources');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [sourcesRes, referralsRes, analyticsRes] = await Promise.all([
        api.get('/referral-sources?entity=our_square'),
        api.get('/housing-referrals?entity=our_square'),
        api.get('/referral-sources/analytics/summary?entity=our_square'),
      ]);
      setSources(sourcesRes.data);
      setReferrals(referralsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const createSource = async () => {
    if (!sourceForm.organization_name || !sourceForm.referral_type) {
      return toast.error('Organization name and type are required');
    }
    try {
      await api.post('/referral-sources', { ...sourceForm, entity: 'our_square' });
      toast.success('Referral source added!');
      setNewSourceOpen(false);
      setSourceForm({ organization_name: '', referral_type: '', contact_name: '', phone: '', email: '', address: '', notes: '' });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const createReferral = async () => {
    if (!referralForm.referral_source_id || !referralForm.resident_name) {
      return toast.error('Source and resident name are required');
    }
    try {
      await api.post('/housing-referrals', { ...referralForm, entity: 'our_square' });
      toast.success('Referral created!');
      setNewReferralOpen(false);
      setReferralForm({ referral_source_id: '', resident_name: '', resident_email: '', resident_phone: '', notes: '', priority: 'medium' });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const updateReferralStatus = async (id, status) => {
    try {
      await api.put(`/housing-referrals/${id}`, { status, status_notes: '' });
      toast.success('Status updated');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  if (selectedSource) {
    return <SourceDetail source={selectedSource} api={api} onBack={() => { setSelectedSource(null); load(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="referral-sources-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Referral Sources</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage referral organizations and track incoming referrals</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={newReferralOpen} onOpenChange={setNewReferralOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full gap-2"><UserPlus size={16} /> Log Referral</Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Log New Referral</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div><Label>Referral Source *</Label>
                  <Select value={referralForm.referral_source_id} onValueChange={v => setReferralForm({ ...referralForm, referral_source_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
                    <SelectContent>{sources.map(s => <SelectItem key={s.id} value={s.id}>{s.organization_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Resident Name *</Label><Input value={referralForm.resident_name} onChange={e => setReferralForm({ ...referralForm, resident_name: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Email</Label><Input value={referralForm.resident_email} onChange={e => setReferralForm({ ...referralForm, resident_email: e.target.value })} /></div>
                  <div><Label>Phone</Label><Input value={referralForm.resident_phone} onChange={e => setReferralForm({ ...referralForm, resident_phone: e.target.value })} /></div>
                </div>
                <div><Label>Priority</Label>
                  <Select value={referralForm.priority} onValueChange={v => setReferralForm({ ...referralForm, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Notes</Label><DictationInput value={referralForm.notes} onChange={e => setReferralForm({ ...referralForm, notes: e.target.value })} rows={2} showCleanup={false} /></div>
                <Button onClick={createReferral} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full">Log Referral</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={newSourceOpen} onOpenChange={setNewSourceOpen}>
            <DialogTrigger asChild>
              <Button className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full gap-2"><Plus size={16} /> Add Source</Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Add Referral Source</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div><Label>Organization Name *</Label><Input value={sourceForm.organization_name} onChange={e => setSourceForm({ ...sourceForm, organization_name: e.target.value })} /></div>
                <div><Label>Referral Type *</Label>
                  <Select value={sourceForm.referral_type} onValueChange={v => setSourceForm({ ...sourceForm, referral_type: v })}>
                    <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(REFERRAL_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Contact Name</Label><Input value={sourceForm.contact_name} onChange={e => setSourceForm({ ...sourceForm, contact_name: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Phone</Label><Input value={sourceForm.phone} onChange={e => setSourceForm({ ...sourceForm, phone: e.target.value })} /></div>
                  <div><Label>Email</Label><Input value={sourceForm.email} onChange={e => setSourceForm({ ...sourceForm, email: e.target.value })} /></div>
                </div>
                <div><Label>Address</Label><Input value={sourceForm.address} onChange={e => setSourceForm({ ...sourceForm, address: e.target.value })} /></div>
                <div><Label>Notes</Label><DictationInput value={sourceForm.notes} onChange={e => setSourceForm({ ...sourceForm, notes: e.target.value })} rows={2} showCleanup={false} /></div>
                <Button onClick={createSource} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full">Add Source</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Analytics Overview */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-cyan-50 text-cyan-700 flex items-center justify-center mb-2"><Building size={18} /></div>
            <p className="text-2xl font-bold text-slate-900">{analytics.total_sources || 0}</p>
            <p className="text-xs text-muted-foreground">Active Sources</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-purple-50 text-purple-700 flex items-center justify-center mb-2"><Users size={18} /></div>
            <p className="text-2xl font-bold text-slate-900">{analytics.total_referrals || 0}</p>
            <p className="text-xs text-muted-foreground">Total Referrals</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center mb-2"><Clock size={18} /></div>
            <p className="text-2xl font-bold text-slate-900">{analytics.pending_referrals || 0}</p>
            <p className="text-xs text-muted-foreground">Pending Review</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center mb-2"><CheckCircle size={18} /></div>
            <p className="text-2xl font-bold text-slate-900">{analytics.accepted_referrals || 0}</p>
            <p className="text-xs text-muted-foreground">Accepted</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1 rounded-lg">
          <TabsTrigger value="sources" className="rounded-md text-xs">Sources ({sources.length})</TabsTrigger>
          <TabsTrigger value="referrals" className="rounded-md text-xs">Referrals ({referrals.length})</TabsTrigger>
          <TabsTrigger value="analytics" className="rounded-md text-xs">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="sources" className="mt-4">
          <div className="grid sm:grid-cols-2 gap-4">
            {sources.map(s => (
              <Card key={s.id} className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedSource(s)} data-testid={`source-${s.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl bg-cyan-50 flex items-center justify-center"><Building size={20} className="text-cyan-600" /></div>
                    <Badge className={TYPE_COLORS[s.referral_type]}>{REFERRAL_TYPE_LABELS[s.referral_type] || s.referral_type}</Badge>
                  </div>
                  <h3 className="font-semibold text-slate-900">{s.organization_name}</h3>
                  {s.contact_name && <p className="text-sm text-muted-foreground mt-1">{s.contact_name}</p>}
                  <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Users size={12} /> {s.total_referrals || 0} referrals</span>
                    <span className="flex items-center gap-1"><CheckCircle size={12} /> {s.accepted_referrals || 0} accepted</span>
                  </div>
                </CardContent>
              </Card>
            ))}
            {sources.length === 0 && (
              <Card className="col-span-full border-0 shadow-sm rounded-xl">
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Building className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p>No referral sources yet</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="referrals" className="mt-4">
          <div className="space-y-2">
            {referrals.map(r => (
              <Card key={r.id} className="border-0 shadow-sm rounded-xl">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold">
                      {r.resident_name?.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{r.resident_name}</p>
                      <p className="text-xs text-muted-foreground">From: {r.referral_source_name} • {new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={r.priority === 'urgent' ? 'bg-red-100 text-red-700' : r.priority === 'high' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}>{r.priority}</Badge>
                    <Select value={r.status} onValueChange={v => updateReferralStatus(r.id, v)}>
                      <SelectTrigger className="w-[120px] h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="accepted">Accepted</SelectItem>
                        <SelectItem value="waitlisted">Waitlisted</SelectItem>
                        <SelectItem value="denied">Denied</SelectItem>
                        <SelectItem value="withdrawn">Withdrawn</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            ))}
            {referrals.length === 0 && (
              <Card className="border-0 shadow-sm rounded-xl">
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p>No referrals yet</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-4">
            {/* Top Sources */}
            <Card className="border-0 shadow-sm rounded-xl">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><TrendingUp size={16} /> Top Referral Sources</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(analytics.top_sources || []).slice(0, 5).map((s, i) => (
                    <div key={s.id} className="flex items-center gap-3">
                      <span className="text-sm font-bold text-muted-foreground w-5">{i + 1}</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{s.organization_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Progress value={s.acceptance_rate} className="h-1.5 flex-1" />
                          <span className="text-xs text-muted-foreground">{s.acceptance_rate}%</span>
                        </div>
                      </div>
                      <span className="text-sm font-bold">{s.total_referrals}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* By Type */}
            <Card className="border-0 shadow-sm rounded-xl">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><BarChart3 size={16} /> Referrals by Type</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(analytics.by_type || {}).filter(([k, v]) => v.total > 0).sort((a, b) => b[1].total - a[1].total).map(([type, data]) => (
                    <div key={type} className="flex items-center justify-between p-2 rounded-lg bg-slate-50">
                      <div className="flex items-center gap-2">
                        <Badge className={TYPE_COLORS[type]}>{REFERRAL_TYPE_LABELS[type]}</Badge>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <span>{data.total} total</span>
                        <span className="text-emerald-600">{data.accepted} accepted</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SourceDetail({ source, api, onBack }) {
  const [fullSource, setFullSource] = useState(null);

  useEffect(() => {
    api.get(`/referral-sources/${source.id}`).then(r => setFullSource(r.data)).catch(console.error);
  }, [source.id]);

  if (!fullSource) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="source-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={onBack} className="rounded-full"><ArrowLeft size={16} /></Button>
        <div>
          <h2 className="text-xl font-bold text-slate-900">{fullSource.organization_name}</h2>
          <Badge className={TYPE_COLORS[fullSource.referral_type]}>{REFERRAL_TYPE_LABELS[fullSource.referral_type]}</Badge>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Contact Information</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            {fullSource.contact_name && <p className="flex items-center gap-2"><Users size={14} className="text-muted-foreground" /> {fullSource.contact_name}</p>}
            {fullSource.phone && <p className="flex items-center gap-2"><Phone size={14} className="text-muted-foreground" /> {fullSource.phone}</p>}
            {fullSource.email && <p className="flex items-center gap-2"><Mail size={14} className="text-muted-foreground" /> {fullSource.email}</p>}
            {fullSource.address && <p className="flex items-center gap-2"><MapPin size={14} className="text-muted-foreground" /> {fullSource.address}</p>}
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Statistics</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Total Referrals</span><span className="font-bold">{fullSource.total_referrals}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Accepted</span><span className="font-bold text-emerald-600">{fullSource.accepted_referrals}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Acceptance Rate</span><span className="font-bold">{fullSource.total_referrals > 0 ? Math.round((fullSource.accepted_referrals / fullSource.total_referrals) * 100) : 0}%</span></div>
          </CardContent>
        </Card>
      </div>

      {fullSource.referrals?.length > 0 && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-base">Referral History</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {fullSource.referrals.map(r => (
                <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div>
                    <p className="font-medium text-sm">{r.resident_name}</p>
                    <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  <Badge className={r.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' : r.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}>{r.status}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
